import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

export type Role = 'SYS_ADMIN' | 'ORG_ADMIN' | 'USER';

export interface Me {
  id: string;
  email: string;
  name: string;
  nameDv?: string | null;
  role: Role;
  orgId: string | null;
  orgName?: string | null;
  isOrgAdmin?: boolean;
  designationDv?: string | null;
  designationEn?: string | null;
  section?: string | null;
  hasSignature?: boolean;
  // Whether the user's org is linked to each sibling app (drives invite
  // "App access" controls).
  hasWapsign?: boolean;
  hasWapmeet?: boolean;
  hasWapstat?: boolean;
  hasWappro?: boolean;
  hasWapdocs?: boolean;
}

export type ProvisionOutcome = 'ok' | 'exists' | 'failed';

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: Me;
  provisioning?: {
    wapsign?: ProvisionOutcome;
    wapmeet?: ProvisionOutcome;
    wapstat?: ProvisionOutcome;
    wappro?: ProvisionOutcome;
    wapdocs?: ProvisionOutcome;
  };
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: Me | null;
  login: (email: string, password: string) => Promise<void>;
  registerViaInvite: (
    inviteToken: string,
    password: string,
    name: string,
  ) => Promise<
    { wapsign?: ProvisionOutcome; wapmeet?: ProvisionOutcome; wapstat?: ProvisionOutcome; wappro?: ProvisionOutcome; wapdocs?: ProvisionOutcome } | undefined
  >;
  refresh: () => Promise<void>;
  // Ambient SSO cold-boot: if the parent-domain wap_sso cookie resolves to an
  // active session, store it (same as login) and return true; else false.
  ssoBootstrap: () => Promise<boolean>;
  logout: () => Promise<void>;
  hydrateMe: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      login: async (email, password) => {
        const r = await axios.post<AuthResponse>('/api/auth/login', { email, password });
        set({ accessToken: r.data.accessToken, refreshToken: r.data.refreshToken, user: r.data.user });
      },
      registerViaInvite: async (inviteToken, password, name) => {
        const r = await axios.post<AuthResponse>('/api/auth/register', { inviteToken, password, name });
        set({ accessToken: r.data.accessToken, refreshToken: r.data.refreshToken, user: r.data.user });
        return r.data.provisioning;
      },
      refresh: async () => {
        const t = get().refreshToken;
        if (!t) throw new Error('no refresh token');
        const r = await axios.post<AuthResponse>('/api/auth/refresh', { refreshToken: t });
        set({ accessToken: r.data.accessToken, refreshToken: r.data.refreshToken, user: r.data.user });
      },
      ssoBootstrap: async () => {
        try {
          // withCredentials so the parent-domain wap_sso cookie is sent.
          const r = await axios.post<AuthResponse | ''>('/api/auth/sso-bootstrap', undefined, {
            withCredentials: true,
          });
          // 204 no_session → axios gives an empty body; nothing to store.
          if (r.status === 204 || !r.data) return false;
          const data = r.data;
          set({ accessToken: data.accessToken, refreshToken: data.refreshToken, user: data.user });
          return true;
        } catch {
          return false;
        }
      },
      logout: async () => {
        const t = get().refreshToken;
        // AWAIT the server logout before navigating: withCredentials so the
        // wap_sso cookie is sent → server revokes the shared SSO session, fans
        // out to all apps, and clears the cookie. If we don't await, the
        // immediate reload below aborts this request, the SSO session survives,
        // and the reloaded /login page's ssoBootstrap() silently logs back in.
        try {
          await axios.post('/api/auth/logout', { refreshToken: t }, { withCredentials: true });
        } catch {
          /* non-fatal — still clear local state + navigate below */
        }
        set({ accessToken: null, refreshToken: null, user: null });
        if (typeof window !== 'undefined') {
          window.location.assign('/login');
        }
      },
      hydrateMe: async () => {
        const a = get().accessToken;
        if (!a) return;
        try {
          const r = await axios.get<{ user: Me }>('/api/users/me', {
            headers: { Authorization: `Bearer ${a}` },
          });
          set({ user: r.data.user });
        } catch {
          /* swallow — refresh will fire on the next API call */
        }
      },
    }),
    { name: 'wapoffice.auth' },
  ),
);
